import { apiClient } from "./api";

export interface ShopVariantGroup {
  name: string;
  options: string[];
}

export interface ShopItem {
  item_id: string;
  name: string;
  description: string;
  price_cents: number;
  currency: string;
  in_stock: boolean;
  photo_public_url: string;
  variants: ShopVariantGroup[];
  sort_order: number;
}

export interface OrderLineItem {
  item_id: string;
  name_snapshot: string;
  unit_price_cents_snapshot: number;
  variant_selections: Record<string, string>;
  quantity: number;
}

export interface ShippingAddress {
  line1: string;
  line2?: string;
  city: string;
  region: string;
  postal_code: string;
  country: "US";
}

export interface Order {
  order_id: string;
  status: "pending_payment" | "paid" | "shipped" | "cancelled";
  customer_name: string;
  customer_email: string;
  // Optional Hash House Harriers nickname; empty string if the customer
  // didn't supply one at checkout.
  hash_name: string;
  shipping_address: ShippingAddress;
  line_items: OrderLineItem[];
  subtotal_cents: number;
  shipping_cents: number;
  tax_cents: number;
  total_cents: number;
  currency: string;
  created_at: string;
}

export interface OrderCreatePayload {
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  hash_name: string;
  shipping_address: ShippingAddress;
  line_items: Array<{
    item_id: string;
    variant_selections: Record<string, string>;
    quantity: number;
  }>;
  customer_notes?: string;
  /** One per checkout, sent again on a retry: the server places the order once. */
  idempotency_key?: string;
}

export interface OrderCreateResponse {
  order_id: string;
  status: string;
  total_cents: number;
  currency: string;
  // Unguessable token the customer needs in `?token=` to view this order on
  // the order page. Same token is also used in the email link.
  view_token: string;
}

export interface ShopStatus {
  enabled: boolean;
  preview: boolean;
  is_admin: boolean;
  // Bare usernames (no @, no URL). Empty string means the admin hasn't
  // configured that handle yet — the storefront should hide that pay button.
  paypal_handle: string;
  venmo_handle: string;
}

const PREVIEW_STORAGE_KEY = "hnt_shop_preview";

const previewListeners = new Set<() => void>();

/** For useSyncExternalStore: told whenever preview mode is turned on or off. */
export function subscribePreview(listener: () => void): () => void {
  previewListeners.add(listener);
  return () => void previewListeners.delete(listener);
}

export function setPreviewMode(on: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (on) window.sessionStorage.setItem(PREVIEW_STORAGE_KEY, "1");
    else window.sessionStorage.removeItem(PREVIEW_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  previewListeners.forEach((listener) => listener());
}

export function isPreviewMode(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(PREVIEW_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function previewParams(): Record<string, string> {
  return isPreviewMode() ? { preview: "1" } : {};
}

export const shopService = {
  getStatus: () => apiClient.get<ShopStatus>("/shop/status", previewParams()),
  listItems: () => apiClient.get<ShopItem[]>("/shop/items", previewParams()),
  getItem: (id: string) => apiClient.get<ShopItem>(`/shop/items/${id}`, previewParams()),
  createOrder: (payload: OrderCreatePayload) => apiClient.post<OrderCreateResponse>("/shop/orders", payload),
  getOrder: (id: string, token?: string) => apiClient.get<Order>(`/shop/orders/${id}`, token ? { token } : undefined),
};
